import { useTranslations } from '@vocab/react';
import React, { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { DiscardDialogProvider } from 'src/app/views/context/DiscardDialogContext';
import { EditFlowSectionsManagerProvider } from 'src/app/views/context/EditFlowSectionsManagerContext';
import { EditFlowDataContext } from 'src/app/views/context/cajaFlowData';
import { useAdvertiserId } from 'src/app/views/hooks/useAdvertiserId';
import { useCurrentUser } from 'src/app/views/hooks/useCurrentUser';
import { useDataCleanup } from 'src/app/views/hooks/useDataCleanup';
import { useEditJobDraft } from 'src/app/views/hooks/useEditJobDraft';
import { useFlowType } from 'src/app/views/hooks/useFlowType';
import { useHirerAccountAndContractBalance } from 'src/app/views/hooks/useHirerAccountAndContractBalance';
import { useIdentity } from 'src/app/views/hooks/useIdentity';
import { useJobData } from 'src/app/views/hooks/useJobData';
import { useJobId } from 'src/app/views/hooks/useJobId';
import { useQueryParams } from 'src/app/views/hooks/useQueryParams';
import { useRefreshProductSetForAdEditing } from 'src/app/views/hooks/useRefreshProductSetForAdEditing';
import { useSelectedRRQuestions } from 'src/app/views/hooks/useSelectedRRQuestions';
import { useTealiumEditOrRefreshFlowType } from 'src/app/views/hooks/useTealiumEditOrRefreshFlowType';
import { SeoTitle } from 'src/app/views/shared/SeoTitle';
import type { ContainerProps } from 'src/types/app';
import type AppState from 'src/types/appState';

import translations from '../.vocab';

const EditFlowContainer = ({ children }: ContainerProps) => {
  const { t } = useTranslations(translations);

  useDataCleanup();
  useFlowType('edit');
  useTealiumEditOrRefreshFlowType('edit');
  useAdvertiserId();
  useJobId();
  useHirerAccountAndContractBalance();
  useCurrentUser();
  useIdentity();
  useSelectedRRQuestions();

  const { loading: isJobDataLoading } = useJobData();
  const { loading: isEditJobDraftLoading } = useEditJobDraft();

  useRefreshProductSetForAdEditing(isJobDataLoading);

  const { getJobIdParam, removeParams } = useQueryParams();

  useEffect(() => {
    const jobIdParam = getJobIdParam();

    if (jobIdParam && !isJobDataLoading && !isEditJobDraftLoading) {
      // remove query params to prevent re-fetching data
      removeParams(['jobid']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditJobDraftLoading, isJobDataLoading]);

  return (
    <EditFlowDataContext.Provider
      value={{
        loading: isJobDataLoading || isEditJobDraftLoading,
      }}
    >
      <SeoTitle title={t('edit-container-seo-title')} />
      <EditFlowSectionsManagerProvider>
        <DiscardDialogProvider>{children}</DiscardDialogProvider>
      </EditFlowSectionsManagerProvider>
    </EditFlowDataContext.Provider>
  );
};

const EditFlowContainerInit = (props: ContainerProps) => {
  const { isDiscarding } = useSelector(
    (state: AppState) => ({
      isDiscarding: state.app.isDiscarding,
    }),
    shallowEqual,
  );

  return isDiscarding ? null : <EditFlowContainer {...props} />;
};

export default EditFlowContainerInit;
